import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";

import { filter, map } from "rxjs/operators";

import { translations } from "@app/i18n";

import { PageConfigurationService } from "@app/shared/services/page-configuration.service";

import { PageContentDialogData } from "@shared/models/page-configuration.model";

import { PageContentDialogComponent } from "@shared/components/page-content-dialog/page-content-dialog.component";

interface Breadcrumb {
  label: string;
  route?: string;
  reload?: boolean;
  type?: "detail";
  param?: string;
  prefix?: string;
  fallback?: string;
  isClickable?: boolean;
  isCurrent?: boolean;
}

interface BreadcrumbState {
  breadcrumbs: Breadcrumb[];
  rootLabel: string;
  moduleLabel: string | null;
  pageId: string | null;
  pageTitle: string | null;
}

@Component({
  selector: "app-breadcrumbs",
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: "./breadcrumbs.component.html",
  styleUrls: ["./breadcrumbs.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly pageConfigService = inject(PageConfigurationService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly translations = translations;

  private readonly breadcrumbState = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.buildBreadcrumbState()),
      takeUntilDestroyed(this.destroyRef),
    ),
    { initialValue: this.buildBreadcrumbState() },
  );

  protected readonly breadcrumbs = computed(
    () => this.breadcrumbState().breadcrumbs,
  );
  protected readonly rootLabel = computed(
    () => this.breadcrumbState().rootLabel,
  );
  protected readonly moduleLabel = computed(
    () => this.breadcrumbState().moduleLabel,
  );
  protected readonly pageId = computed(() => this.breadcrumbState().pageId);
  protected readonly pageTitle = computed(
    () => this.breadcrumbState().pageTitle,
  );
  protected readonly showManageContent = computed(() => this.pageId() !== null);

  protected onBreadcrumbClick(item: Breadcrumb): void {
    if (item.reload) {
      this.reloadCurrentRoute();
    } else if (item.route) {
      this.router.navigateByUrl(item.route);
    }
  }

  protected onManagePageContent(): void {
    const pageId = this.pageId();

    if (!pageId) {
      console.warn("No page ID configured for this route");
      return;
    }

    const storedConfig = this.pageConfigService.getPageConfiguration(pageId);
    const sections = storedConfig
      ? storedConfig.sections
      : this.pageConfigService.getPageSections(pageId);

    const dialogData: PageContentDialogData = {
      pageId,
      sections,
      pageTitle: this.pageTitle() ?? "Page Content",
    };

    const dialogRef = this.dialog.open(PageContentDialogComponent, {
      maxWidth: "366px",
      data: dialogData,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && pageId) {
        this.pageConfigService.updatePageConfiguration(pageId, result.sections);
      }
    });
  }

  private buildBreadcrumbState(): BreadcrumbState {
    const targetRoute = this.findDeepestRouteWithBreadcrumb(this.route.root);

    if (!targetRoute) {
      return {
        breadcrumbs: [],
        rootLabel: "",
        moduleLabel: null,
        pageId: null,
        pageTitle: null,
      };
    }

    const data = targetRoute.snapshot.data;
    const rootLabel = data["breadcrumb"] ?? "";
    const moduleLabel = data["breadcrumbModule"] ?? null;
    const pageId = data["pageId"] ?? null;
    const pageTitle = data["pageTitle"] ?? null;

    const trail = (data["breadcrumbTrail"] ?? []) as Breadcrumb[];
    const breadcrumbs = trail
      .map((item, idx, arr) => ({
        ...item,
        label:
          item.type === "detail"
            ? this.resolveParamLabel(item, targetRoute)
            : item.label,
        isClickable: !!(item.route || item.reload),
        isCurrent: idx === arr.length - 1,
      }))
      .filter((item) => item.label);

    return {
      breadcrumbs,
      rootLabel,
      moduleLabel,
      pageId,
      pageTitle,
    };
  }

  private resolveParamLabel(item: Breadcrumb, route: ActivatedRoute): string {
    const paramValue = route.snapshot.paramMap.get(item.param ?? "") ?? "";
    if (paramValue) {
      return `${item.prefix ?? ""}${paramValue}`.trim();
    }
    return item.fallback ?? item.prefix ?? "";
  }

  private findDeepestRouteWithBreadcrumb(
    route: ActivatedRoute,
  ): ActivatedRoute | null {
    let current: ActivatedRoute | null = route;
    let candidate: ActivatedRoute | null = null;

    while (current) {
      if (current.snapshot.data?.["breadcrumb"]) {
        candidate = current;
      }
      current = current.firstChild ?? null;
    }

    return candidate;
  }

  private reloadCurrentRoute() {
    const currentUrl = this.router.url;
    this.router
      .navigateByUrl("/", { skipLocationChange: true })
      .then(() => this.router.navigateByUrl(currentUrl));
  }
}
